import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Tooltip, Typography } from '@material-ui/core';
import { AlertTriangle, Info } from 'lucide-react';
import React from 'react';
import { FlexContainer } from '../../layout/Flex';

const LabelContainer = styled('div')<{ variant?: string }>`
  display: flex;
  align-items: baseline;
  gap: ${(p) => p.theme.spacing(0.5)}px;
  flex-wrap: wrap;
  margin-bottom: ${(p) =>
    p.variant === 'secondary'
      ? `${p.theme.spacing(0.5)}px`
      : `${p.theme.spacing()}px`};
  margin-top: ${(p) =>
    p.variant === 'secondary' ? `-${p.theme.spacing()}px` : ''};
`;

export const MainLabel = styled('label')<{ variant?: string }>`
  font-weight: ${(p) =>
    p.variant === 'secondary' ? '' : p.theme.typography.fontWeightMedium};
  font-size: ${(p) =>
    p.variant === 'secondary' ? `${p.theme.custom.fontSize.xs}px` : ''};
  color: ${(p) =>
    p.variant === 'secondary' ? p.theme.custom.colors.gray.w500 : ''};
`;

const InfoContainer = styled('div')`
  margin-left: ${(p) => p.theme.spacing(0.5)}px;
  align-self: center;
  display: flex;
  align-items: center;
`;

const Container = styled('div')<{ fullWidth: boolean; minWidth?: number }>`
  ${(p) => (p.fullWidth ? 'width: 100%;' : '')}
  ${(p) => (p.minWidth !== undefined ? `min-width: ${p.minWidth}px;` : '')}
`;

export const Label = ({
  label,
  subLabel,
  info,
  warning,
  variant,
  otherElements
}: {
  label: React.ReactNode;
  info?: string;
  warning?: string;
  subLabel?: React.ReactNode;
  variant?: string;
  otherElements?: React.ReactNode;
}) => {
  const theme = useTheme();
  return (
    <LabelContainer variant={variant}>
      <MainLabel variant={variant}>{label}</MainLabel>
      {subLabel && <Typography variant="caption">{subLabel}</Typography>}
      {info && (
        <InfoContainer>
          <Tooltip title={info}>
            <Info size={14} />
          </Tooltip>
        </InfoContainer>
      )}
      {warning && (
        <InfoContainer>
          <Tooltip title={warning}>
            <AlertTriangle size={16} color={theme.custom.colors.yellow.w700} />
          </Tooltip>
        </InfoContainer>
      )}
      {otherElements && <InfoContainer>{otherElements}</InfoContainer>}
    </LabelContainer>
  );
};

const BLANK_TEXT_NODE = <span>&#8203;</span>;

export const CustomInputLabel: React.FC<{
  label?: React.ReactNode;
  subLabel?: React.ReactNode;
  info?: string;
  warning?: string;
  fullWidth?: boolean;
  minWidth?: number;
  variant?: string;
  otherElements?: React.ReactNode;
}> = ({
  label = BLANK_TEXT_NODE,
  subLabel,
  info,
  warning,
  fullWidth,
  minWidth,
  variant,
  children,
  otherElements
}) => {
  return (
    <Container fullWidth={!!fullWidth} minWidth={minWidth}>
      <Label
        label={label}
        subLabel={subLabel}
        info={info}
        warning={warning}
        variant={variant}
        otherElements={otherElements}
      />
      <FlexContainer>{children}</FlexContainer>
    </Container>
  );
};
